/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { capitalizeFirst } from 'modules/common/helpers/strings';
import { TAGS } from 'modules/scenario/constants';

/**
 * Generates a simple unique ID using the current timestamp and a random number.
 *
 * @returns {string} A pseudo-unique string ID.
 */
const getRandomId = () => Date.now() + Math.random();

export default getRandomId;

/**
 * Get selected breakdown based on isDetailed toggle and selected breakdown type
 * @param {Array} visualFilters
 * @param {String} chartBreakdown
 * @param {Boolean} isDetailed
 * @returns
 */
export const getSelectedBreakdown = (visualFilters, chartBreakdown, isDetailed) => {
  const breakdown = visualFilters?.find((filter) => filter?.id === chartBreakdown);
  //
  const type = breakdown?.type;
  const subType = breakdown?.values?.[0]?.value;
  //
  if (isDetailed) {
    if (breakdown?.tags?.includes(TAGS.DETAILED)) {
      return type;
    }
    return subType;
  }
  if (breakdown?.tags?.includes(TAGS.DETAILED)) {
    return subType;
  }
  return type;
};
/**
 * Get selected sub breakdown filter
 * @param {Array} visualFilters - List of registered visual filters
 * @param {String} tag - tag
 * @returns
 */
export const getSubBreakdownFilter = (visualFilters, tag = null) => {
  let subBreakdownFilter;
  if (tag !== null) {
    subBreakdownFilter = visualFilters
      ?.filter((filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN && tag))
      .map((data) => ({
        id: data?.id,
        label: data?.values[0]?.label,
      }));
  } else {
    subBreakdownFilter = visualFilters
      ?.filter((filter) => filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN))
      .map((data) => ({
        label: data?.values[0]?.label,
        id: data?.id,
      }));
  }
  //
  return subBreakdownFilter;
};
/**
 * Get selected focus on filter
 * @param {Array} breakdownData - list of breakdown data
 * @param {String} selectedBreakdown - selected breakdown value
 * @returns
 */
export const getFocusOnFilter = (breakdownData, selectedBreakdown) => {
  const focusOnFilter = [];
  breakdownData
    ?.filter(
      (filter) => capitalizeFirst(filter?.breakdownType) === capitalizeFirst(selectedBreakdown)
    )
    .forEach((breakdown) =>
      breakdown?.subCategory?.forEach((data) =>
        focusOnFilter.push({
          label: data,
          id: focusOnFilter.length,
        })
      )
    );
  const focusOnFilterValues = focusOnFilter.filter(
    (obj, index, array) => array.findIndex((o) => o.label === obj.label) === index
  );
  //
  return focusOnFilterValues;
};
/**
 * Get selected sub pace filter
 * @param {Array} visualFilters - List of registered visual filters
 * @returns
 */
export const getSubPaceFilter = (visualFilters) => {
  const subPaceFilter = visualFilters
    ?.filter((filter) => filter?.tags?.includes(TAGS.PACE_HORIZON))
    .map((data) => ({
      label: data?.values,
      id: data?.id,
    }));
  //
  return subPaceFilter;
};

/**
 * Get percentage items list
 * @param {Array} visualFilters - List of registered visual filters
 * @returns
 */
export const getPercentageList = (visualFilters) => {
  const percentageItemList = visualFilters
    ?.filter((filter) => filter?.tags?.includes(TAGS.PERCENTAGE))
    .map((data) => ({
      label: `${data?.values}%`,
      id: data?.id,
      value: Number(data?.values),
    }))
    .sort((a, b) => a.value - b.value)
    .map(({ label, id }) => ({ label, id }));
  //
  return percentageItemList;
};
/**
 * Get selected date comparison filter
 * @param {Array} visualFilters - List of registered visual filters
 * @returns
 */
export const getDateComparisonFilter = (visualFilters) => {
  const dateComparisonFilter = visualFilters
    ?.find((filter) => filter?.tags?.includes(TAGS.DATE_COMPARISON))
    .values.map((data) => data.label);
  //
  return dateComparisonFilter;
};
/**
 * Extracts the base dropdown option from a formatted label.
 *
 * @param {string} label - The formatted label from the dropdown.
 * @returns {string} - The base dropdown option corresponding to the label.
 *
 * @example
 * getActualOption('Next 2 Days'); // returns 'Next Days'
 * getActualOption('Past 5 Weeks'); // returns 'Past Weeks'
 * getActualOption('Today'); // returns 'Today'
 */
export const getActualOption = (label) => {
  // Only process labels that start with "Next" or "Past"
  const match = label.match(/^(Next|Past)\s+\d+\s+(Days|Weeks|Months)$/);
  if (match) {
    const [, when, unit] = match;
    return `${when} ${unit}`;
  }
  return label;
};
